/**
 * Générateur — Automatisme : donner le double, le triple, la moitié, le
 * prédécesseur, le successeur, le carré d'un nombre.
 * Attendu ID : 1110 | Niveau : 4ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 10, ligne 710 :
 * « Donner le double, le triple, la moitié, le prédécesseur, le successeur,
 * le carré d'un nombre. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * DEUX REGISTRES. Le neuf est LA LETTRE (objectif 5e l. 606, mise en œuvre
 * Eduscol 2026 p. 13 : « il exprime le double, le triple, la moitié, le
 * carré, le successeur, le prédécesseur d'un nombre n » ; c'est ce que la
 * banque seule retient, 4NE10). Le registre numérique est de l'entretien
 * (CM2, 1037 sans le triple, DEPP 4e 2023 q.2) et sert de CONTRÔLE de la
 * formule (p. 13 : « en les testant pour les nombres dont il connait déjà la
 * valeur »). Wrapper de 932 rejeté (objectif multi-cartes). Le carré
 * numérique des entiers 0..12 est 1101 : ici le carré est LITTÉRAL (n²),
 * numérique seulement comme discriminant du double.
 * Frontières : quadruple, tiers, quart, cube (banque) exclus ; relatifs et
 * carré d'un décimal exclus (À ARBITRER) ; « n ÷ 2 » pour la moitié : n/2
 * seul (À ARBITRER).
 * MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 4NE10 « Exprimer
 * une transformation à l'aide de n », littéral seul, six questions.
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes en
 *     texte : trois numériques (successeur ou prédécesseur d'un entier ;
 *     moitié d'un entier, impair une fois sur deux ; double d'un entier ou
 *     d'un décimal en ,5) et une littérale à réponse tapable (« le triple de
 *     n : 3n » ou « le double de n : 2n »).
 *   Carte 2 « Avec la lettre » (lettre_qcm_unique) : « Le double de n,
 *     c'est… » QUATRE choix 2n (clé), n + 2 (ajout_pour_double), n² (carre),
 *     n/2 (moitie) ; carré : n², 2n (double_pour_carre), n + 2 (trois choix,
 *     n × n jamais : même valeur) ; moitié : n/2, 2n, n − 2, 2/n
 *     (inversion) ; successeur / prédécesseur : n + 1, n − 1 (sens_inverse),
 *     2n, n + 2 ; triple : 3n, n + 3, n³, 3/n ; numérique une fois sur cinq :
 *     « La moitié de 4,5 » 2,25, 2,5 (chiffres_accoles), 9, 2,2.
 *   Carte 3 « Les six mots » (mots_relier) : quatre mots tirés parmi les
 *     six à relier à leur expression en n, une cible en trop (n + 2, le
 *     piège « de plus / fois plus » de 1038).
 *
 * INVARIANTS : entiers 1..100, décimaux en ,5 pour le double seulement ;
 * lettre n deux fois sur trois, sinon x ou t ; chaque valeur numérique
 * re-calculée (throw).
 * SACRIFIÉ (fiche d'aide à venir, « le double de 6, c'est 6 × 2 = 12 ; le
 * double de n, c'est n × 2 = 2n ») : (double ; 6) en numérique ; la carte
 * littérale « le double de n » reste, résidu assumé (À ARBITRER).
 *
 * ── Erreurs (familles nommées) ───────────────────────────────────────────
 *   ajout_pour_double : n + 2 (le piège « de plus / fois plus » de 1038) ;
 *   moitie_moins      : n − 2 ;
 *   inversion         : 2/n, 3/n (nom de 1049) ;
 *   sens_inverse      : successeur ↔ prédécesseur, double ↔ moitié (1037) ;
 *   double_pour_carre : n² → 2n (1109, 1101) ; carre : 2n → n² ;
 *   chiffres_accoles  : moitié de 4,5 → 2,5 (DEPP 6e 2025 q.13, 43,6 %) ;
 *   moitie_voisine    : moitié de 70 → 45, 40 (DEPP 4e 2023 q.2 ; nom de 1056).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeRelier, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1110;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Avec la lettre';
const L_RELIER = 'Les six mots';

const LETTRES = ['n', 'n', 'x', 't'];
const MOTS = ['double', 'triple', 'moitie', 'predecesseur', 'successeur', 'carre'];

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple de la fiche : le double de 6, sacrifié en numérique. */
export const SACRIFIE = { mot: 'double', n: 6 };

/** dixièmes → écriture française. */
const fr = (d) => (d % 10 === 0 ? String(d / 10) : `${Math.floor(d / 10)},${d % 10}`);

/** Le libellé numérique et la réponse (dixièmes → texte) ; assert par un second calcul. */
const NUMERIQUE = {
    double: (d) => ({ libelle: `Le double de ${fr(d)}`, reponse: 2 * d, controle: d + d }),
    triple: (d) => ({ libelle: `Le triple de ${fr(d)}`, reponse: 3 * d, controle: d + d + d }),
    moitie: (d) => ({ libelle: `La moitié de ${fr(d)}`, reponse: d / 2, controle: d - d / 2 }),
    predecesseur: (d) => ({ libelle: `Le prédécesseur de ${fr(d)}`, reponse: d - 10, controle: d - 10 }),
    successeur: (d) => ({ libelle: `Le successeur de ${fr(d)}`, reponse: d + 10, controle: d + 10 }),
};
const itemNumerique = (mot, d) => {
    if (mot === SACRIFIE.mot && d === SACRIFIE.n * 10) throw new Error('gen_1110 : item sacrifié');
    const it = NUMERIQUE[mot](d);
    if (!Number.isInteger(it.reponse) || it.reponse !== it.controle || it.reponse < 0) throw new Error(`gen_1110 : ${mot} de ${d} dixièmes`);
    return { mot, d, ...it, texte: fr(it.reponse) };
};
const tirerNumerique = (mot) => {
    for (let essai = 0; essai < 60; essai++) {
        let d;
        if (mot === 'double') d = randomChoice([true, false]) ? randInt(2, 60) * 10 : randInt(1, 30) * 10 + 5; // entier ou en ,5
        else if (mot === 'triple') d = randInt(2, 30) * 10;
        else if (mot === 'moitie') d = randInt(2, 100) * 10;                                                  // pair → entier, impair → ,5
        else d = randInt(1, 99) * 10;                                                                          // successeur / prédécesseur : entiers
        if (mot === SACRIFIE.mot && d === SACRIFIE.n * 10) continue;
        return itemNumerique(mot, d);
    }
    return itemNumerique(mot, 200);
};

/** Les expressions littérales : { texte tapable, KaTeX, cible du relier }. */
const LITTERAL = {
    double: (L) => ({ tape: `2${L}`, katex: `2${L}` }),
    triple: (L) => ({ tape: `3${L}`, katex: `3${L}` }),
    moitie: (L) => ({ tape: null, katex: `\\dfrac{${L}}{2}` }),
    predecesseur: (L) => ({ tape: null, katex: `${L} - 1` }),
    successeur: (L) => ({ tape: null, katex: `${L} + 1` }),
    carre: (L) => ({ tape: null, katex: `${L}^2` }),
};
const LIBELLE = { double: 'Le double', triple: 'Le triple', moitie: 'La moitié', predecesseur: 'Le prédécesseur', successeur: 'Le successeur', carre: 'Le carré' };

const EXPLICATIONS = {
    ajout_pour_double: 'Le double, c\'est deux FOIS le nombre : 2n, pas n + 2.',
    moitie_moins: 'La moitié, c\'est le nombre partagé en deux : n/2, pas n − 2.',
    inversion: 'La moitié de n, c\'est n divisé par 2, pas 2 divisé par n.',
    sens_inverse: 'Le successeur vient après (n + 1), le prédécesseur avant (n − 1) ; le double multiplie, la moitié partage.',
    double_pour_carre: 'Le carré de n, c\'est n × n = n², pas 2n.',
    carre: 'Le double de n, c\'est 2n : le carré n² serait n × n.',
    chiffres_accoles: 'La moitié de 4,5 n\'est pas 2,5 : 4,5 = 4 + 0,5, moitié 2 + 0,25 = 2,25.',
    moitie_voisine: 'Je vérifie : le double de ma réponse doit redonner le nombre.',
    regle: 'Le double de n : 2n. Le triple : 3n. La moitié : n/2. Le successeur : n + 1. Le prédécesseur : n − 1. Le carré : n². J\'essaie avec un nombre pour vérifier.',
};

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ─────────────

/** Code commun de chaque leurre littéral, dans l'ordre de DISTRACTEURS ; null si la construction ne dit pas l'erreur nommée (3/n, n/2 et n − 2 du prédécesseur, n + 2 du successeur). */
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : un code par mot, l'erreur n'est pas la même pour le double,
// la moitié, le successeur ou le carré.
const CODES_LETTRE = {
    double: ['double_ecrit_en_somme', 'double_ecrit_en_puissance', 'mot_reciproque'],
    triple: ['double_ecrit_en_somme', 'double_ecrit_en_puissance', null],
    moitie: ['mot_reciproque', 'moitie_ecrite_moins_2', 'moitie_ecrite_a_l_envers'],
    predecesseur: ['mot_reciproque', null, null],
    successeur: ['mot_reciproque', 'successeur_ecrit_en_double', null],
    carre: ['carre_ecrit_en_double', 'base_plus_exposant'],
};

/** Clé de comparaison d'une réponse : sa valeur si c'est un nombre (3,25 = 3.250), son texte sinon. */
const cleReponse = (v) => {
    const s = String(v).replace(/\s/g, '').replace(',', '.');
    return s !== '' && Number.isFinite(Number(s)) ? `n${Number(s)}` : `t${s.toLowerCase()}`;
};

/** Les erreurs nommables parmi des candidats { code, valeur } (code null : non rangé) : jamais la bonne réponse, jamais une valeur que deux codes donnent. */
const nommer = (candidats, bonne) => {
    const groupes = new Map();
    candidats.forEach((c) => {
        const k = cleReponse(c.valeur);
        if (k !== cleReponse(bonne)) groupes.set(k, [...(groupes.get(k) || []), c]);
    });
    return [...groupes.values()]
        .filter((g) => g[0].code && g.every((c) => c.code === g[0].code))
        .map((g) => ({ code: g[0].code, valeur: String(g[0].valeur) }));
};

/** Range les erreurs nommées dans la question (rien si aucune). */
const ranger = (payload, erreurs) => {
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};

/** Série flash, trou par trou : le mot réciproque pour un nombre (en dixièmes) ; pour la lettre, k seul et les leurres du QCM tapés (n+2, n², n/2). */
const erreursFlash = (phrase, items, L, motLettre) => phrase.split('\n').flatMap((ligne, trou) => {
    const it = items.find((x) => ligneNumerique(x) === ligne);
    const k = motLettre === 'double' ? 2 : 3;
    let nommees = [];
    if (it) {
        const reciproque = { double: it.d / 2, moitie: 2 * it.d, successeur: it.d - 10, predecesseur: it.d + 10 }[it.mot];
        nommees = nommer([{ code: 'mot_reciproque', valeur: String(reciproque / 10).replace('.', ',') }], it.texte);
    } else if (ligne === `$\\text{${LIBELLE[motLettre]} de } ${L} \\text{ : } {{${LITTERAL[motLettre](L).tape}}}$`) {
        nommees = nommer([
            { code: 'x_oublie', valeur: String(k) }, { code: 'double_ecrit_en_somme', valeur: `${L}+${k}` },
            { code: 'double_ecrit_en_puissance', valeur: `${L}^${k}` }, { code: 'double_ecrit_en_puissance', valeur: `${L}${k === 2 ? '²' : '³'}` },
            ...(k === 2 ? [{ code: 'mot_reciproque', valeur: `${L}/2` }] : []),
        ], LITTERAL[motLettre](L).tape);
    }
    return nommees.map(({ code, valeur }) => ({ code, trou, valeur }));
});

// ─── Carte 1 : Série flash ────────────────────────────────────────────────
// Lignes entières en KaTeX (règle prof 2026-09-12 : la lettre en mode maths
// partout, et une phrase à trous ne mélange pas texte nu et $…$) : le libellé
// en \text, le nombre avec sa virgule KaTeX {,}, le trou en fin de ligne.

/** « $\text{Le successeur de } 37 \text{ : } {{38}}$ » ; la réponse tapée reste en texte (« 2,25 »). */
const ligneNumerique = (it) => `$\\text{${it.libelle.replace(/ \d.*$/, '')} } ${fr(it.d).replace(',', '{,}')} \\text{ : } {{${it.texte}}}$`;

const genFlash = () => {
    for (let essai = 0; essai < 60; essai++) {
        const L = randomChoice(LETTRES);
        const items = [tirerNumerique(randomChoice(['successeur', 'predecesseur'])), tirerNumerique('moitie'), tirerNumerique('double')];
        if (new Set(items.map((it) => it.d)).size < 3) continue;
        const motLettre = randomChoice(['double', 'triple']);
        const lignes = shuffle([
            ...items.map(ligneNumerique),
            `$\\text{${LIBELLE[motLettre]} de } ${L} \\text{ : } {{${LITTERAL[motLettre](L).tape}}}$`,
        ]);
        const payload = makeTexteTrou(lignes.join('\n'), `<p><b>Complète.</b> Pour la lettre, écris l'expression (ex. : ${tex(`5${L}`)}).</p>`, { ...opts(L_FLASH), mode: 'saisie' });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        ranger(payload, erreursFlash(payload.contenu.phrase, items, L, motLettre));
        payload._meta.params = { lettre: L, items: items.map(({ mot, d, texte }) => ({ mot, d, texte })), motLettre };
        return payload;
    }
    const items = [itemNumerique('successeur', 370), itemNumerique('moitie', 70), itemNumerique('double', 65)];
    const lignes = [...items.map(ligneNumerique), '$\\text{Le triple de } n \\text{ : } {{3n}}$'];
    const payload = makeTexteTrou(lignes.join('\n'), `<p><b>Complète.</b> Pour la lettre, écris l'expression (ex. : ${tex('5n')}).</p>`, { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    ranger(payload, erreursFlash(payload.contenu.phrase, items, 'n', 'triple'));
    payload._meta.params = { lettre: 'n', items: items.map(({ mot, d, texte }) => ({ mot, d, texte })), motLettre: 'triple' };
    return payload;
};

// ─── Carte 2 : QCM « Avec la lettre » ─────────────────────────────────────

const DISTRACTEURS = {
    double: (L) => [[`${L} + 2`, 'ajout_pour_double'], [`${L}^2`, 'carre'], [`\\dfrac{${L}}{2}`, 'sens_inverse']],
    triple: (L) => [[`${L} + 3`, 'ajout_pour_double'], [`${L}^3`, 'carre'], [`\\dfrac{3}{${L}}`, 'inversion']],
    moitie: (L) => [[`2${L}`, 'sens_inverse'], [`${L} - 2`, 'moitie_moins'], [`\\dfrac{2}{${L}}`, 'inversion']],
    predecesseur: (L) => [[`${L} + 1`, 'sens_inverse'], [`\\dfrac{${L}}{2}`, 'moitie_moins'], [`${L} - 2`, 'moitie_moins']],
    successeur: (L) => [[`${L} - 1`, 'sens_inverse'], [`2${L}`, 'ajout_pour_double'], [`${L} + 2`, 'ajout_pour_double']],
    carre: (L) => [[`2${L}`, 'double_pour_carre'], [`${L} + 2`, 'ajout_pour_double']],
};

const genQcm = () => {
    if (randInt(1, 5) === 1) {
        // numérique : la moitié d'un nombre en ,5 (le piège des chiffres accolés)
        for (let essai = 0; essai < 60; essai++) {
            const d = randInt(1, 20) * 10 + 5; // 1,5 .. 20,5
            const moitie = d * 5; // en centièmes
            const frC = (c) => (c % 100 === 0 ? String(c / 100) : c % 10 === 0 ? `${Math.floor(c / 100)},${(c % 100) / 10}` : `${Math.floor(c / 100)},${String(c % 100).padStart(2, '0')}`);
            const bon = frC(moitie);
            const faux = [
                { texte: frC(Math.floor(d / 10) * 50 + 50), code: 'chiffres_accoles' }, // 4,5 → 2,5 (la moitié de 4, puis le 5 recopié)
                { texte: frC(d * 20), code: 'sens_inverse' },                            // le double
                { texte: frC(moitie - 5), code: 'moitie_voisine' },                      // 2,2 pour 2,25
            ];
            if (new Set([bon, ...faux.map((f) => f.texte)]).size !== 4) continue;
            const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, `<p><b>La moitié de ${fr(d)}, c'est…</b></p>`, opts(L_QCM));
            payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
            // la partie entière partagée et le 5 recopié (4,5 → 2,5) seulement si elle est paire ; 2,2 pour 2,25 : la division arrêtée
            const codes = { chiffres_accoles: Math.floor(d / 10) % 2 === 0 ? 'partie_decimale_recopiee' : null, sens_inverse: 'mot_reciproque', moitie_voisine: 'reste_oublie' };
            ranger(payload, nommer(faux.map((f) => ({ code: codes[f.code], valeur: f.texte })), bon));
            payload._meta.params = { registre: 'numerique', mot: 'moitie', d, distracteurs: faux.map(({ code }) => ({ code })) };
            return payload;
        }
    }
    const L = randomChoice(LETTRES);
    const mot = randomChoice(MOTS);
    const bon = tex(LITTERAL[mot](L).katex);
    const faux = DISTRACTEURS[mot](L).map(([k, code]) => ({ texte: tex(k), code }));
    const payload = makeQcm([bon, ...faux.map((f) => f.texte)], bon, `<p><b>${LIBELLE[mot]} de ${tex(L)}, c'est…</b></p>`, opts(L_QCM));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    ranger(payload, nommer(faux.map((f, i) => ({ code: CODES_LETTRE[mot][i], valeur: f.texte })), bon));
    payload._meta.params = { registre: 'lettre', lettre: L, mot, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 3 : Relier (4 mots, 4 expressions, 1 leurre) ───────────────────

const genRelier = () => {
    const L = randomChoice(LETTRES);
    const mots = shuffle([...MOTS]).slice(0, 4);
    // la lettre en mode maths dans la source aussi (MathText rend « $n$ » en ligne)
    const pairs = mots.map((mot) => ({ source: `${LIBELLE[mot]} de $${L}$`, target: tex(LITTERAL[mot](L).katex) }));
    const payload = makeRelier(
        pairs,
        '<p><b>Relie chaque mot à son expression.</b> Une expression est en trop.</p>',
        { ...opts(L_RELIER), cibles_leurres: [{ target: tex(`${L} + 2`), code: 'lecture_additive' }] },
    );
    payload.contenu = { ...payload.contenu, explications: { ajout_pour_double: EXPLICATIONS.ajout_pour_double, regle: EXPLICATIONS.regle } };
    payload._meta.params = { lettre: L, mots, leurre: { texte: `${L} + 2`, code: 'ajout_pour_double' } };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'la clé est une ÉCRITURE en n ; jamais une écriture de même valeur (n × n pour n², n ÷ 2 pour n/2) en distracteur',
        'sacrifié : le double de 6 en numérique ; la carte littérale « le double de n » reste (résidu assumé)',
    ],
    qcm_unique: {
        support: 'un des six mots avec la lettre (quatre fois sur cinq), la moitié d\'un nombre en ,5 sinon',
        familles: {
            ajout_pour_double: 'n + 2, n + 3 : « fois plus » lu « de plus » (1038)',
            carre: 'n² pour le double, n³ pour le triple',
            sens_inverse: 'n − 1 pour n + 1, 2n pour n/2 (1037)',
            moitie_moins: 'n − 2 pour la moitié',
            inversion: '2/n, 3/n (nom de 1049)',
            double_pour_carre: '2n pour n² (1109, 1101)',
            chiffres_accoles: 'la moitié de 4,5 → 2,5 (DEPP 6e 2025 q.13, 43,6 %)',
            moitie_voisine: 'la moitié de 4,5 → 2,2 (nom de 1056)',
        },
    },
    relier: 'quatre mots parmi six, une cible en trop : n + 2',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    lettre_qcm_unique: genQcm,
    mots_relier: genRelier,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'lettre', label: L_QCM, formatsList: [{ key: 'lettre_qcm_unique', label: 'QCM' }] },
    { id: 'mots', label: L_RELIER, formatsList: [{ key: 'mots_relier', label: 'Relier' }] },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    lettre_qcm_unique: ['reste_oublie'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
